import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import { 
  ArrowLeft, 
  Trophy, 
  Users, 
  Plus, 
  Share2, 
  Settings, 
  Calendar,
  Target,
  Crown,
  UserPlus,
  Copy,
  Check,
  Trash2
} from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { useUserSpots } from '@/hooks/useUserSpots'
import { Leaderboard } from '@/components/ui/leaderboard'
import { ShareDialog } from '@/components/ui/share-dialog'
import {
  getUserTrips,
  getTripById,
  joinTripByShareToken,
  deleteTrip,
  resetTripProgress,
  updateTrip
} from '@/lib/firestore'
import type { Trip } from '@/types'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export default function TripPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useUser()
  const { toast } = useToast()
  const { switchTrackingMode, trackingMode } = useUserSpots()

  // State
  const [userTrips, setUserTrips] = useState<Trip[]>([])
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null)
  const [loading, setLoading] = useState(true)
  const [shareDialogOpen, setShareDialogOpen] = useState(false)
  const [shareDialogType, setShareDialogType] = useState<'all-time' | 'create-trip'>('create-trip')
  const [joinToken, setJoinToken] = useState('')
  const [joiningTrip, setJoiningTrip] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const [editingTripName, setEditingTripName] = useState(false)
  const [newTripName, setNewTripName] = useState('')
  const [copied, setCopied] = useState(false)

  // Check for share token in URL
  useEffect(() => {
    const shareToken = searchParams.get('join')
    if (shareToken && user) {
      handleJoinTrip(shareToken)
    }
  }, [searchParams, user])

  // Load user trips
  useEffect(() => {
    if (user?.id) {
      loadUserTrips()
    }
  }, [user])

  // Switch to trip mode when component mounts
  useEffect(() => {
    if (trackingMode !== 'trip-mode') {
      switchTrackingMode('trip-mode')
    }
  }, [trackingMode, switchTrackingMode])

  const loadUserTrips = async () => {
    if (!user?.id) return

    try {
      setLoading(true)
      const trips = await getUserTrips(user.id)
      setUserTrips(trips)
      
      // Auto-select first trip if none selected
      if (trips.length > 0 && !selectedTrip) {
        setSelectedTrip(trips[0])
      }
    } catch (error) {
      console.error('Error loading trips:', error)
      toast({
        title: "Error",
        description: "Failed to load your trips. Please refresh the page.",
        variant: "destructive",
        duration: 5000,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleJoinTrip = async (shareToken: string) => {
    if (!user?.id) return

    try {
      setJoiningTrip(true)
      const result = await joinTripByShareToken(
        shareToken, 
        user.id, 
        user.fullName || user.firstName || 'Anonymous'
      )

      if (result.success && result.tripId) {
        toast({
          title: "Trip Joined!",
          description: "You've successfully joined the competition trip.",
          duration: 3000,
        })
        
        // Load the joined trip
        const trip = await getTripById(result.tripId)
        if (trip) {
          setUserTrips(prev => [...prev, trip])
          setSelectedTrip(trip)
        }
        
        // Clear URL parameter
        navigate('/trip', { replace: true })
      } else {
        toast({
          title: "Failed to Join",
          description: result.error || "Could not join the trip.",
          variant: "destructive",
          duration: 5000,
        })
      }
    } catch (error) {
      console.error('Error joining trip:', error)
      toast({
        title: "Error",
        description: "Failed to join trip. Please try again.",
        variant: "destructive",
        duration: 5000,
      })
    } finally {
      setJoiningTrip(false)
    }
  }

  const handleCreateTrip = () => {
    setShareDialogType('create-trip')
    setShareDialogOpen(true)
  }

  const handleTripCreated = (tripId: string) => {
    setShareDialogOpen(false)
    loadUserTrips()
    
    // Auto-select the new trip
    getTripById(tripId).then(trip => {
      if (trip) {
        setSelectedTrip(trip)
      }
    })
  }

  const handleDeleteTrip = async () => {
    if (!selectedTrip || !user?.id) return

    try {
      const success = await deleteTrip(selectedTrip.id, user.id)
      if (success) {
        toast({
          title: "Trip Deleted",
          description: "The trip has been permanently deleted.",
          duration: 3000,
        })
        
        setUserTrips(prev => prev.filter(t => t.id !== selectedTrip.id))
        setSelectedTrip(userTrips.find(t => t.id !== selectedTrip.id) || null)
      } else {
        toast({
          title: "Error",
          description: "Only the trip creator can delete the trip.",
          variant: "destructive",
          duration: 3000,
        })
      }
    } catch (error) {
      console.error('Error deleting trip:', error)
      toast({
        title: "Error",
        description: "Failed to delete trip. Please try again.",
        variant: "destructive",
        duration: 3000,
      })
    } finally {
      setShowDeleteConfirm(false)
    }
  }

  const handleResetTrip = async () => {
    if (!selectedTrip || !user?.id) return

    try {
      const success = await resetTripProgress(selectedTrip.id, user.id)
      if (success) {
        toast({
          title: "Trip Reset",
          description: "All trip progress has been reset for all participants.",
          duration: 3000,
        })
      } else {
        toast({
          title: "Error",
          description: "Only the trip creator can reset the trip.",
          variant: "destructive",
          duration: 3000,
        })
      }
    } catch (error) {
      console.error('Error resetting trip:', error)
      toast({
        title: "Error",
        description: "Failed to reset trip. Please try again.",
        variant: "destructive",
        duration: 3000,
      })
    } finally {
      setShowResetConfirm(false)
    }
  }

  const handleUpdateTripName = async () => {
    if (!selectedTrip || !user?.id || !newTripName.trim()) return

    try {
      await updateTrip(selectedTrip.id, { name: newTripName.trim() })
      
      const updatedTrip = { ...selectedTrip, name: newTripName.trim() }
      setSelectedTrip(updatedTrip)
      setUserTrips(prev => prev.map(t => t.id === selectedTrip.id ? updatedTrip : t))
      
      toast({
        title: "Trip Updated",
        description: "Trip name has been updated.",
        duration: 3000,
      })
    } catch (error) {
      console.error('Error updating trip:', error)
      toast({
        title: "Error",
        description: "Failed to update trip name.",
        variant: "destructive",
        duration: 3000,
      })
    } finally {
      setEditingTripName(false)
      setNewTripName('')
    }
  }

  const copyShareLink = async () => {
    if (!selectedTrip) return

    const shareUrl = `${window.location.origin}/trip?join=${selectedTrip.shareToken}`
    
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      
      toast({
        title: "Link Copied!",
        description: "Share link has been copied to your clipboard.",
        duration: 2000,
      })
      
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      toast({
        title: "Failed to Copy",
        description: "Please copy the link manually from the address bar.",
        variant: "destructive",
        duration: 3000,
      })
    }
  }

  const isCreator = (trip: Trip) => trip.creatorId === user?.id

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#42a5f5] to-[#1976d2] py-6 shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/dashboard')}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white">Competition Trips</h1>
                <p className="text-blue-100">Compete with friends to spot the most teams!</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                onClick={() => {
                  setShareDialogType('all-time')
                  setShareDialogOpen(true)
                }}
                variant="outline"
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share Progress
              </Button>
              <Button
                onClick={handleCreateTrip}
                className="bg-white text-[#42a5f5] hover:bg-gray-100"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Trip
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {userTrips.length === 0 ? (
          /* No Trips State */
          <div className="max-w-2xl mx-auto text-center py-16">
            <Trophy className="h-16 w-16 mx-auto mb-6 text-gray-300" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Compete?
            </h2>
            <p className="text-gray-600 mb-8">
              Create a trip to compete with friends, or join an existing trip with a share link.
            </p>
            
            <div className="space-y-4">
              <Button
                onClick={handleCreateTrip}
                size="lg"
                className="w-full sm:w-auto"
              >
                <Plus className="h-5 w-5 mr-2" />
                Create Your First Trip
              </Button>
              
              <div className="text-gray-500">or</div>
              
              <div className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
                <Input
                  placeholder="Enter trip join code or paste share link"
                  value={joinToken}
                  onChange={(e) => setJoinToken(e.target.value)}
                  disabled={joiningTrip}
                />
                <Button
                  onClick={() => handleJoinTrip(joinToken)}
                  disabled={!joinToken.trim() || joiningTrip}
                  variant="outline"
                >
                  {joiningTrip ? (
                    <div className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full" />
                  ) : (
                    <UserPlus className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Trip List */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm border">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Your Trips</h3>
                </div>
                <div className="p-4 space-y-3">
                  {userTrips.map((trip) => (
                    <button
                      key={trip.id}
                      onClick={() => setSelectedTrip(trip)}
                      className={`w-full text-left p-4 rounded-lg border transition-colors ${
                        selectedTrip?.id === trip.id
                          ? 'border-[#42a5f5] bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 truncate">
                            {trip.name}
                          </h4>
                          <p className="text-sm text-gray-500 mt-1">
                            {trip.participants.length} participant{trip.participants.length !== 1 ? 's' : ''}
                          </p>
                          {trip.description && (
                            <p className="text-xs text-gray-400 mt-1 truncate">
                              {trip.description}
                            </p>
                          )}
                        </div>
                        <div className="flex-shrink-0 flex items-center space-x-1">
                          {isCreator(trip) && (
                            <Crown className="h-4 w-4 text-yellow-500" />
                          )}
                          {trip.isActive && (
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Join Trip */}
              <div className="mt-6 bg-white rounded-lg shadow-sm border p-6">
                <h4 className="font-medium text-gray-900 mb-4">Join a Trip</h4>
                <div className="space-y-3">
                  <Input
                    placeholder="Enter share link or join code"
                    value={joinToken}
                    onChange={(e) => setJoinToken(e.target.value)}
                    disabled={joiningTrip}
                  />
                  <Button
                    onClick={() => handleJoinTrip(joinToken)}
                    disabled={!joinToken.trim() || joiningTrip}
                    className="w-full"
                  >
                    {joiningTrip ? (
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        <span>Joining...</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <UserPlus className="h-4 w-4" />
                        <span>Join Trip</span>
                      </div>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Trip Details & Leaderboard */}
            <div className="lg:col-span-2">
              {selectedTrip ? (
                <div className="space-y-6">
                  {/* Trip Header */}
                  <div className="bg-white rounded-lg shadow-sm border p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {editingTripName ? (
                          <div className="flex items-center space-x-2">
                            <Input
                              value={newTripName}
                              onChange={(e) => setNewTripName(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handleUpdateTripName()}
                              placeholder="Trip name"
                              className="text-xl font-bold"
                            />
                            <Button onClick={handleUpdateTripName} size="sm">
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button 
                              onClick={() => {
                                setEditingTripName(false)
                                setNewTripName('')
                              }} 
                              variant="outline" 
                              size="sm"
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <h2 className="text-2xl font-bold text-gray-900">{selectedTrip.name}</h2>
                            {isCreator(selectedTrip) && (
                              <Button
                                onClick={() => {
                                  setEditingTripName(true)
                                  setNewTripName(selectedTrip.name)
                                }}
                                variant="ghost"
                                size="sm"
                              >
                                <Settings className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        )}
                        
                        {selectedTrip.description && (
                          <p className="text-gray-600 mt-2">{selectedTrip.description}</p>
                        )}
                        
                        <div className="flex items-center space-x-4 mt-4 text-sm text-gray-500">
                          <div className="flex items-center space-x-1">
                            <Users className="h-4 w-4" />
                            <span>{selectedTrip.participants.length} participants</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-4 w-4" />
                            <span>Created {new Date(selectedTrip.createdAt).toLocaleDateString()}</span>
                          </div>
                          {isCreator(selectedTrip) && (
                            <div className="flex items-center space-x-1">
                              <Crown className="h-4 w-4 text-yellow-500" />
                              <span>Creator</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Button
                          onClick={copyShareLink}
                          variant="outline"
                          size="sm"
                        >
                          {copied ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                        
                        {isCreator(selectedTrip) && (
                          <>
                            <Button
                              onClick={() => setShowResetConfirm(true)}
                              variant="outline"
                              size="sm"
                              className="text-orange-600 hover:text-orange-700"
                            >
                              Reset
                            </Button>
                            <Button
                              onClick={() => setShowDeleteConfirm(true)}
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Leaderboard */}
                  <Leaderboard
                    type="trip"
                    tripId={selectedTrip.id}
                    currentUserId={user?.id}
                  />
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
                  <Target className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Select a Trip
                  </h3>
                  <p className="text-gray-500">
                    Choose a trip from the list to view the leaderboard and compete with friends.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Share Dialog */}
      <ShareDialog
        isOpen={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        type={shareDialogType}
        onTripCreated={handleTripCreated}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Trip</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedTrip?.name}"? This will permanently remove the trip and all associated data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteTrip}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Trip
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Confirmation */}
      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Trip Progress</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reset all progress for "{selectedTrip?.name}"? This will remove all spotted teams for all participants. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleResetTrip}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Reset Progress
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}